import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {CSS_RESET} from 'styles/Utilities';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';

import {BLUE} from './Colors';

export enum CheckboxSize {
  SMALL = 'SMALL',
  MEDIUM = 'MEDIUM',
}

export interface CheckboxProps {
  checked: boolean;
  disabled?: boolean;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  size?: CheckboxSize;
  dataTestAttrs?: DataTestAttrs;
}

/**
 * Renders a 16x16 checkbox.
 *
 * This component is controlled, so the caller should handle updating the `checked` prop when `onChange` is
 * called.
 *
 * @example
 * function Example() {
 *   const [checked, setChecked] = useState(false);
 *
 *   return (
 *     <Checkbox checked={checked} onChange={() => setChecked(!checked)} />
 *   );
 * }
 */
export default function Checkbox({
  checked,
  disabled = false,
  onChange,
  size = CheckboxSize.MEDIUM,
  dataTestAttrs = {},
}: CheckboxProps): JSX.Element {
  return (
    <input
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={disabled ? undefined : onChange}
      className={css(
        styles.wrapper,
        size === CheckboxSize.SMALL && styles.small,
        size === CheckboxSize.MEDIUM && styles.medium
      )}
      readOnly={true}
      {...getTestProps(dataTestAttrs)}
    />
  );
}

const styles = StyleSheet.create({
  wrapper: {
    // Reset margin and padding from less input[type="checkbox"] styles
    ...CSS_RESET,
    accentColor: BLUE,
    ':disabled': {
      cursor: 'not-allowed',
    },
  },
  small: {
    // Default input size in html
    width: 13,
    height: 13,
  },
  medium: {
    width: 16,
    height: 16,
  },
});
